import { createContext, useContext } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { get, isApiError, post } from './api'
import type { Me, MemberRole, OrgSummary, User } from './types'

/**
 * The session is an httpOnly cookie. This module never sees a token and must
 * never store one: GET /me is the only way the SPA learns who is signed in.
 */
export const SESSION_KEY = ['me'] as const

/** Phase 2 renamed the `owner` role to `admin`; accept either from the api. */
export const normalizeRole = (r: string): MemberRole => (r === 'owner' || r === 'admin' ? 'admin' : 'member')

/**
 * What /me actually sends, as distinct from what types.ts declares. Two fields
 * drifted between the Nest DTO and this hand-written contract, and
 * src/mocks/server.ts was written against the contract rather than the api, so
 * the whole test suite agreed with the wrong shape:
 *
 *   isSuperAdmin   the api nests it under `user`; the SPA reads it top-level.
 *                  Silent failure — `undefined ?? false` sent every platform
 *                  admin to the "you're not part of an organisation" screen.
 *   impersonating  the api sends { org: { id, name, slug }, startedAt,
 *                  readOnly }; ImpersonationBanner wants { orgId, orgName }.
 *
 * Both shapes are accepted here, at the one boundary, rather than in the six
 * call sites. This type goes away when `pnpm codegen` builds types from
 * openapi.json — at which point drift like this is a compile error instead of
 * a blank screen.
 */
type MeWire = Omit<Me, 'user' | 'isSuperAdmin' | 'impersonating'> & {
  user: User & { isSuperAdmin?: boolean }
  isSuperAdmin?: boolean
  impersonating?:
    | { orgId: string; orgName: string; startedAt: string }
    | { org: { id: string; name: string; slug?: string }; startedAt: string; readOnly?: boolean }
    | null
}

function normalizeImpersonating(i: MeWire['impersonating']): Me['impersonating'] {
  if (!i) return null
  return 'org' in i ? { orgId: i.org.id, orgName: i.org.name, startedAt: i.startedAt } : i
}

/** Fill fields an older api doesn't send yet, and map legacy role names. */
export function normalizeMe(raw: MeWire): Me {
  const org = (o: OrgSummary): OrgSummary => ({ ...o, role: normalizeRole(o.role) })
  return {
    ...raw,
    orgs: raw.orgs.map(org),
    activeOrg: raw.activeOrg ? org(raw.activeOrg) : null,
    isSuperAdmin: raw.isSuperAdmin ?? raw.user.isSuperAdmin ?? false,
    impersonating: normalizeImpersonating(raw.impersonating),
  }
}

async function fetchSession(): Promise<Me | null> {
  try {
    return normalizeMe(await get<MeWire>('/me'))
  } catch (e) {
    if (isApiError(e, 401)) return null
    throw e
  }
}

/** `data` is Me when signed in, null when signed out, undefined while loading. */
export function useSession() {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: fetchSession,
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

/**
 * RequireAuth provides the session through context rather than letting every
 * component read the query. When the session ends (logout, a 401), query
 * subscribers re-render BEFORE their parent — reading the query directly they
 * would see null mid-render. Context only changes when RequireAuth re-renders,
 * and by then it is redirecting.
 */
export const MeContext = createContext<Me | null>(null)

/** The signed-in session. Only call below <RequireAuth>, which guarantees it. */
export function useMe(): Me {
  const me = useContext(MeContext)
  if (!me) throw new Error('useMe() called outside <RequireAuth>')
  return me
}

/** The org the session acts in. Only below <RequireAuth> (without allowNoOrg), which guarantees one. */
export function useActiveOrg(): OrgSummary {
  const org = useMe().activeOrg
  if (!org) throw new Error('useActiveOrg() called where no org is guaranteed')
  return org
}

/**
 * Permission hooks. Every mutating control asks one of these.
 *
 * Impersonation ("View as this org") is read-only on the server. The UI hides
 * or disables writes too — a button that looks live but always fails is worse
 * than no button.
 */
export const useReadOnly = (): boolean => useMe().impersonating !== null
/** Any signed-in member can do this (assign, note, resolve…) — unless read-only. */
export const useCanWrite = (): boolean => !useReadOnly()
/** Org admins only (stores, couriers, thresholds, team…) — and never while read-only. */
export const useIsAdmin = (): boolean => {
  const me = useMe()
  return me.activeOrg?.role === 'admin' && me.impersonating === null
}

/** Why a control is unavailable, for its tooltip. null = it isn't. */
export function useWriteBlockReason(adminOnly = false): string | null {
  const me = useMe()
  if (me.impersonating) return 'Read-only while viewing as platform admin'
  if (adminOnly && me.activeOrg?.role !== 'admin') return 'Only an organisation admin can do this'
  return null
}

/** Keep the session, drop every org-scoped cache entry. */
function useResetOrgData() {
  const qc = useQueryClient()
  return (me: Me) => {
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_KEY[0] })
    qc.setQueryData(SESSION_KEY, me)
  }
}

/** Switch the org the session acts in. Every cached query belonged to the old one. */
export function useSwitchOrg() {
  const reset = useResetOrgData()
  return useMutation({
    mutationFn: async (orgId: string) => normalizeMe(await post<MeWire>('/me/active-org', { orgId })),
    onSuccess: reset,
  })
}

/**
 * Accept an invitation. Two paths:
 * - New user (signed out): POST /auth/signup { token, password, name }. The api
 *   creates the account (or sets the password of a provisioned first admin),
 *   joins the inviting org only, and sets the session cookies. 409 with slug
 *   `account-exists` = this email already has a password: sign in instead.
 * - Signed in (an existing account): POST /invitations/accept { token }, then
 *   act in the org it granted.
 * 410 on either = expired or already used.
 */
export function useAcceptInvite() {
  const establish = useEstablishSession()
  return useMutation({
    mutationFn: async (input: { token: string; password?: string; name?: string }) => {
      if (input.password !== undefined) {
        await post('/auth/signup', { token: input.token, password: input.password, name: input.name })
        return
      }
      const res = await post<{ orgId: string; role: string }>('/invitations/accept', { token: input.token })
      await post<MeWire>('/me/active-org', { orgId: res.orgId })
    },
    // Refetch /me: the signup path only now has a session at all.
    onSuccess: establish,
  })
}

/**
 * Super admin: enter an org read-only. The api answers with the impersonation,
 * not the session, so /me is refetched — and every cached query belonged to the
 * previous context.
 */
export function useStartImpersonation() {
  const establish = useEstablishSession()
  return useMutation({
    mutationFn: (orgId: string) => post('/admin/impersonate', { orgId }),
    onSuccess: establish,
  })
}

/** Allowed while read-only (it's how you leave), even if the super-admin flag was revoked meanwhile. */
export function useExitImpersonation() {
  const establish = useEstablishSession()
  return useMutation({
    mutationFn: () => post('/admin/impersonate/stop'),
    onSuccess: establish,
  })
}

export interface LoginInput {
  email: string
  password: string
}

/** Refetch /me rather than trusting the login response's shape for the session. */
function useEstablishSession() {
  const qc = useQueryClient()
  return async () => {
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_KEY[0] })
    // staleTime 0: the cached "signed out" null from a moment ago would otherwise
    // count as fresh under the client's default staleTime and be returned as-is.
    await qc.fetchQuery({ queryKey: SESSION_KEY, queryFn: fetchSession, staleTime: 0 })
  }
}

export function useLogin() {
  const establish = useEstablishSession()
  return useMutation({
    mutationFn: (input: LoginInput) => post('/auth/login', input),
    onSuccess: establish,
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => post('/auth/logout'),
    // Clear even if the request fails: the user asked to leave, and a stale
    // cache of another org's orders must not survive into the next login.
    onSettled: () => {
      qc.setQueryData(SESSION_KEY, null)
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_KEY[0] })
    },
  })
}

/**
 * Where to go after login. Only same-origin paths — `next=//evil.com` or
 * `next=https://…` would turn the login page into an open redirect.
 */
export function safeNext(next: string | null): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return '/'
  return next
}
